import React, { CSSProperties } from 'react';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import ArrowLeft from '@material-ui/icons/ArrowLeft';
import Draggable from 'react-draggable';
import Paper from '@material-ui/core/Paper';
import { styles } from './Modal.styles';
import { useLayoutContext } from '../../Context/Layout.context';
import CloseButton from '../buttons/CloseButtton.component';
import { PaperProps } from '@material-ui/core/Paper';

export interface ModalProps extends WithStyles<typeof styles> {
  children: React.ReactNode;
  open: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  id?: string;
  title?: string | React.ReactNode;
  titlePostscript?: string | React.ReactNode;
  subtitle?: React.ReactNode;
  navButtonLabel?: React.ReactNode;
  autoHeight?: boolean;
  actions?: boolean;
  heightReducing?: boolean;
  isHideBackdrop?: boolean;
  fullScreen?: boolean;
  isPageInModal?: boolean;
  draggable?: boolean;
  xCloseButton?: () => void;
  handleClose?: () => void;
  handleNavBack?: () => void;
  style?: CSSProperties;
}

function PaperComponent(props: PaperProps) {
  return (
    <Draggable
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} />
    </Draggable>
  );
}

export const Modal = withStyles(styles)((props: ModalProps) => {
  const { isTooltipOpen } = useLayoutContext();

  const {
    children,
    classes,
    title,
    titlePostscript,
    subtitle,
    navButtonLabel,
    size = 'md',
    xCloseButton,
    handleNavBack,
    autoHeight,
    heightReducing,
    actions,
    isHideBackdrop,
    fullScreen,
    isPageInModal,
    draggable,
    handleClose,
    style,
    ...other
  } = props;

  const closeDialog = () => {
    handleClose?.() ?? xCloseButton?.();
  };

  return (
    <Dialog
      onClose={closeDialog}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && !isTooltipOpen.current) {
          closeDialog();
        }
      }}
      fullWidth={true}
      maxWidth={size}
      hideBackdrop={isHideBackdrop}
      PaperComponent={draggable ? PaperComponent : undefined}
      classes={{
        paperWidthLg: classes.lg,
        paperWidthXl: heightReducing ? classes.xlLimits : classes.xl,
        paperWidthXs: classes.xs,
      }}
      style={style}
      {...other}
      fullScreen={fullScreen}
      aria-label="modal"
    >
      {(title || navButtonLabel || xCloseButton) && (
        <DialogTitle
          id="draggable-dialog-title"
          style={{
            backgroundColor: isPageInModal ? '#EBECF4' : '',
            height: isPageInModal ? '10px' : '',
            cursor: draggable ? 'move' : undefined,
          }}
        >
          <div aria-label="Nagłowek dialogu" style={{ display: 'flex' }}>
            {title === null && <div aria-label="Tytuł dialogu" style={{ display: 'flex', height: '60px' }}></div>}
            {title && (
              <div aria-label="tytuł" style={{ flexGrow: 3, textAlign: navButtonLabel ? 'center' : undefined }}>
                <div className={classes.title}>{title}</div>
              </div>
            )}
            {titlePostscript && (
              <div aria-label="postscript" style={{ flexGrow: 1, textAlign: 'right' }}>
                <div className={classes.titlePostscript}>{titlePostscript}</div>
              </div>
            )}
          </div>
          {subtitle && (
            <div aria-label="opis" className={classes.subtitle} style={{ textAlign: 'center' }}>
              {subtitle}
            </div>
          )}
        </DialogTitle>
      )}
      {navButtonLabel && (
        <button
          onClick={handleNavBack}
          className={classes.navButton}
          style={{ position: 'relative', top: !title ? '-20px' : '', marginBottom: !title ? '20px' : '' }}
        >
          <div aria-label="Powrót" className={classes.navIcon}>
            <ArrowLeft />
          </div>
          {navButtonLabel}
        </button>
      )}
      {xCloseButton && (
        <div
          onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              xCloseButton();
            }
          }}
        >
          <CloseButton ariaLabel="Zamknij" onClick={xCloseButton} />
        </div>
      )}
      <DialogContent
        style={{ padding: isPageInModal ? '0px' : '', backgroundColor: isPageInModal ? '#EBECF4' : '' }}
        className={[
          autoHeight && !actions ? classes.contentAutoHeight : classes.content,
          actions ? classes.contentHeightWithActions : classes.content,
        ].join(' ')}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
});
